/**
 * @file: AudioRenderer.tsx
 *
 * A voice message (`audio` payload). Full form delegates to the waveform
 * `AudioMessageBubble`; `quoted` renders a compact "mic + duration" pill.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayloadAudio } from '@euler/model/chat';
import { Mic } from 'lucide-react-native';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { AudioMessageBubble } from '../AudioMessageBubble';
import { formatDuration } from './mediaLayout';
import { PayloadRendererProps } from './types';

interface Props extends PayloadRendererProps<ChatMessagePayloadAudio> {
  /** Received audio not yet played → red unread dot (full form only). */
  audioUnread?: boolean;
  /** Fired when a received audio is first played (clears its dot). */
  onAudioPlay?: () => void;
}

export const AudioRenderer: FC<Props> = memo(
  ({ payload, isMine, quoted, audioUnread, onAudioPlay }) => {
    const { rpx } = useResponsiveContext();

    if (quoted) {
      return (
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}
        >
          <Mic color="#64748B" size={rpx(15)} />
          <Typography.Text size={rpx(13)} color="#475569">
            {formatDuration(payload.durationMs)}
          </Typography.Text>
        </View>
      );
    }

    return (
      <AudioMessageBubble
        payload={payload}
        isMine={isMine}
        unread={audioUnread}
        onFirstPlay={onAudioPlay}
      />
    );
  },
);
